import { useRef, useEffect, useState } from 'react'
import { init, use } from 'echarts/core'
import type { ComposeOption, ECharts } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import type { LineSeriesOption } from 'echarts/charts'
import { GridComponent, TooltipComponent, MarkAreaComponent } from 'echarts/components'
import type { GridComponentOption, TooltipComponentOption, MarkAreaComponentOption } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import SunCalc from 'suncalc3'
import tzlookup from 'tz-lookup'

use([LineChart, GridComponent, TooltipComponent, MarkAreaComponent, CanvasRenderer])

type ECOption = ComposeOption<LineSeriesOption | GridComponentOption | TooltipComponentOption | MarkAreaComponentOption>
import type { ParamData, ParamConfig, Trace, WindUnit, PrecipMode, Stamping, Strip } from './types.js'
import { WIND_UNITS } from './types.js'

const MODEL_STYLE: Record<string, { label: string; color: string }> = {
  'aifs':          { label: 'AIFS',    color: '#2563eb' },
  'ifs':           { label: 'IFS',     color: '#d97706' },
  'icon-d2':       { label: 'ICON-D2', color: '#059669' },
}

// Precomputed exp(-0.5*z*z) lookup table for z in [0, 4] at 0.01 resolution
const EXP_RES = 100  // entries per unit of z
const EXP_LUT = new Float32Array(401)
for (let i = 0; i <= 400; i++) { const z = i / EXP_RES; EXP_LUT[i] = Math.exp(-0.5 * z * z) }

// Plasma colormap — 256-entry RGB lookup, interpolated from 11 matplotlib control points
const PLASMA_CTRL: [number, number, number][] = [
  [13, 8, 135], [62, 4, 156], [99, 0, 167], [137, 3, 165], [170, 31, 156],
  [199, 55, 139], [223, 84, 115], [242, 117, 93], [252, 155, 65], [254, 201, 42], [240, 249, 33],
]
const PLASMA = new Uint8ClampedArray(256 * 3)
for (let i = 0; i < 256; i++) {
  const x = (i / 255) * (PLASMA_CTRL.length - 1)
  const lo = Math.floor(x), hi = Math.min(Math.ceil(x), PLASMA_CTRL.length - 1)
  const f = x - lo
  PLASMA[i * 3]     = PLASMA_CTRL[lo][0] * (1 - f) + PLASMA_CTRL[hi][0] * f
  PLASMA[i * 3 + 1] = PLASMA_CTRL[lo][1] * (1 - f) + PLASMA_CTRL[hi][1] * f
  PLASMA[i * 3 + 2] = PLASMA_CTRL[lo][2] * (1 - f) + PLASMA_CTRL[hi][2] * f
}

// Density → color/alpha: alpha ramps fast (gamma 0.5), hue compensated (gamma 0.65)
// to counteract alpha-over-white compression at the low end
const ALPHA_GAMMA = 0.5
const HUE_GAMMA = 0.65

/** Write a plasma-colored pixel into an RGBA buffer at the given index.
 *  d is density in [0, 1]. */
function plasmaPixel(buf: Uint8ClampedArray, idx: number, d: number) {
  const huePos = Math.min(Math.floor(Math.pow(d, HUE_GAMMA) * 255), 255)
  const cOff = huePos * 3
  buf[idx]     = PLASMA[cOff]
  buf[idx + 1] = PLASMA[cOff + 1]
  buf[idx + 2] = PLASMA[cOff + 2]
  buf[idx + 3] = Math.min(Math.round(Math.pow(d, ALPHA_GAMMA) * 255), 255)
}

export function DensityScale() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const w = 200, h = 10
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')!
    const img = ctx.createImageData(w, h)
    for (let x = 0; x < w; x++) {
      const d = x / (w - 1)
      for (let y = 0; y < h; y++) plasmaPixel(img.data, (y * w + x) * 4, d)
    }
    ctx.putImageData(img, 0, 0)
    for (let pct = 20; pct < 100; pct += 20) {
      ctx.fillStyle = 'rgba(0,0,0,0.3)'
      ctx.fillRect(Math.round((pct / 100) * (w - 1)), 0, 1, h)
    }
  }, [])
  return <canvas ref={canvasRef} style={{ width: 120, height: 8, borderRadius: 2 }} />
}

// Fixed meteorological color scales (value → RGB)
// Wind speed (km/h): calm → strong → extreme
// Windy meteogram wind scale — from _shared-additional-colors.js "windDetail"
// Source values in m/s, converted to km/h. Windy interpolates in YUV space.
const WIND_SCALE: [number, number, number, number][] = [
  [  0.0, 255, 255, 255],  // 0 m/s — white
  [ 10.8, 255, 255, 255],  // 3 m/s — still white
  [ 14.4,   0, 200, 254],  // 4 m/s — cyan
  [ 21.6,   0, 230,   0],  // 6 m/s — green
  [ 36.0, 254, 174,   0],  // 10 m/s — orange
  [ 68.4, 254,   0, 150],  // 19 m/s — magenta
  [360.0, 151,  50, 222],  // 100 m/s — purple
]
// Precip rate (mm/h) — Windy map overlay defaults
const PRECIP_SCALE: [number, number, number, number][] = [
  [0,    255, 255, 255],
  [0.1,  180, 215, 255],
  [0.2,   53, 154, 255],
  [0.5,    4, 130, 255],
  [1,      0, 105, 210],
  [2,      0,  54, 127],
  [3,     20, 143,  27],
  [5,     99, 237,   7],
  [7,    232, 220,   0],
  [10,   255, 166, 106],
  [14,   247,  30,  84],
  [20,   136,   0,   0],
  [30,   194,   0, 251],
  [60,   235, 166, 255],
  [100,  212, 212, 212],
]

// Pressure-level wind (km/h). Calibrated for the 20-180 km/h range
// that dominates 850 hPa weather, with headroom for 200+ km/h jet
// streaks. Same colour family as the surface scale so it reads as
// "wind" at a glance, just shifted toward higher magnitudes.
const PL_WIND_SCALE: [number, number, number, number][] = [
  [  0.0, 255, 255, 255],  // calm — white
  [ 20.0, 200, 230, 255],  // 5.5 m/s — pale cyan
  [ 40.0, 100, 200, 255],  // 11 m/s — cyan
  [ 70.0,   0, 200, 100],  // 19 m/s — green
  [110.0, 255, 200,   0],  // 31 m/s — yellow-orange
  [160.0, 255, 100,   0],  // 44 m/s — orange-red
  [220.0, 255,   0, 150],  // 61 m/s — magenta
  [320.0, 150,  50, 220],  // 89 m/s — purple (rare jet)
]

// Cloud cover (%): clear=white, overcast=grey
const CLOUD_SCALE: [number, number, number, number][] = [
  [  0, 255, 255, 255],
  [ 50, 210, 210, 210],
  [100, 120, 120, 120],
]
// Column water vapour (mm). Typical mid-latitude range 5-30; tropical
// up to ~60. Same blue family as precip but calibrated for the
// instantaneous-column-loading magnitudes, not rate-of-fall.
const MOISTURE_SCALE: [number, number, number, number][] = [
  [ 0,   255, 255, 255],
  [10,   200, 230, 255],
  [20,   100, 180, 255],
  [30,    30, 130, 230],
  [45,     0,  80, 180],
  [60,     0,  40, 130],
]

function scaleColor(value: number, scale: [number, number, number, number][]): [number, number, number, number] {
  if (value <= scale[0][0]) return [scale[0][1], scale[0][2], scale[0][3], 255]
  for (let i = 1; i < scale.length; i++) {
    if (value <= scale[i][0]) {
      const f = (value - scale[i - 1][0]) / (scale[i][0] - scale[i - 1][0])
      return [
        Math.round(scale[i - 1][1] * (1 - f) + scale[i][1] * f),
        Math.round(scale[i - 1][2] * (1 - f) + scale[i][2] * f),
        Math.round(scale[i - 1][3] * (1 - f) + scale[i][3] * f),
        255,
      ]
    }
  }
  const last = scale[scale.length - 1]
  return [last[1], last[2], last[3], 255]
}

function invisibleSeries(data: ParamData, transform?: (t: Trace) => (number | null)[]): LineSeriesOption[] {
  return data.traces.filter(t => t.member_type !== 'an').map(t => {
    const vals = transform ? transform(t) : t.values
    return {
      type: 'line' as const,
      data: t.times.map((ms, i) => [ms, vals[i]]),
      lineStyle: { width: 0, color: 'transparent' },
      itemStyle: { color: 'transparent' },
      showSymbol: false, silent: true, symbol: 'none',
    }
  })
}

function nightBands(xMin: number, xMax: number, lat: number, lon: number): [number, number][] {
  const bands: [number, number][] = []
  const d = new Date(xMin)
  d.setUTCHours(12, 0, 0, 0)
  d.setUTCDate(d.getUTCDate() - 1) // start day before to catch initial night

  while (d.getTime() < xMax) {
    const times = SunCalc.getSunTimes(d, lat, lon)
    const sunset = times.sunsetEnd.value.getTime()
    // Next day's sunrise
    const next = new Date(d)
    next.setUTCDate(next.getUTCDate() + 1)
    const nextTimes = SunCalc.getSunTimes(next, lat, lon)
    const sunrise = nextTimes.sunriseStart.value.getTime()

    if (sunrise > xMin && sunset < xMax) {
      bands.push([Math.max(sunset, xMin), Math.min(sunrise, xMax)])
    }
    d.setUTCDate(d.getUTCDate() + 1)
  }
  return bands
}

// Offset (local wall clock - UTC) in ms at instant t, for an IANA timezone.
// en-US fixes the calendar (Gregorian) and digits (ASCII) so the parts parse as
// numbers; hourCycle h23 keeps midnight as 0 rather than 24.
function tzOffsetMs(t: number, tz: string): number {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(t)
  const g = (k: string) => +p.find(x => x.type === k)!.value
  return Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second')) - t
}

// Epoch ms of every local midnight in [xMin, xMax]. Offset is recomputed at
// each candidate so a DST transition mid-window shifts subsequent boundaries.
function localMidnights(xMin: number, xMax: number, tz: string): number[] {
  const out: number[] = []
  const DAY = 86400000
  let mid = Math.floor((xMin + tzOffsetMs(xMin, tz)) / DAY) * DAY - tzOffsetMs(xMin, tz)
  while (mid <= xMax) {
    const corrected = Math.floor((mid + tzOffsetMs(mid, tz)) / DAY) * DAY - tzOffsetMs(mid, tz)
    if (corrected >= xMin) out.push(corrected)
    mid = corrected + DAY
  }
  return out
}

// Epoch ms of the local midnight starting the day that contains t.
function dayStart(t: number, tz: string): number {
  const off = tzOffsetMs(t, tz)
  return Math.floor((t + off) / 86400000) * 86400000 - off
}

function percentiles(values: number[], ps: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b)
  return ps.map(p => {
    const i = (p / 100) * (sorted.length - 1)
    const lo = Math.floor(i), hi = Math.ceil(i)
    return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo)
  })
}

function ensembleStats(traces: Trace[], axisTime: number): { p10: number; p50: number; p90: number } | null {
  const pfTraces = traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  if (pfTraces.length < 2) return null
  const values: number[] = []
  for (const t of pfTraces) {
    // A trace that doesn't span this instant contributes nothing. Without this
    // the nearest-slot search clamps to whichever end is closer and quotes that
    // reading under a timestamp it has nothing to say about — a lagged cycle
    // that finished hours ago would still sit in the spread being hovered.
    if (!t.times.length || axisTime < t.times[0] || axisTime > t.times[t.times.length - 1]) continue
    let best = 0, bestDiff = Math.abs(t.times[0] - axisTime)
    for (let i = 1; i < t.times.length; i++) {
      const diff = Math.abs(t.times[i] - axisTime)
      if (diff < bestDiff) { best = i; bestDiff = diff }
    }
    const v = t.values[best]
    if (v !== null) values.push(v)
  }
  // Members absent at this time drop out, so re-check the count: percentiles
  // over fewer than two readings aren't a spread.
  if (values.length < 2) return null
  const [p10, p50, p90] = percentiles(values, [10, 50, 90])
  return { p10, p50, p90 }
}

// Value to paint at `time`, given a cursor `si` with times[si] <= time <=
// times[si+1]. Two readings either side interpolate as usual. A reading with no
// reading next to it on the relevant side instead holds for half the local grid
// step in that direction — so a member sampled more coarsely than the run's grid
// draws a band around each of its readings rather than nothing at all, and the
// reading at either end of a trace covers its own half-step too.
function sampleAt(times: number[], values: (number | null)[], si: number, time: number): number | null {
  const a = values[si]
  const b = si + 1 < times.length ? values[si + 1] : null

  if (a !== null && b !== null) {
    const span = times[si + 1] - times[si]
    const f = span > 0 ? Math.min(Math.max((time - times[si]) / span, 0), 1) : 0
    return a * (1 - f) + b * f
  }
  if (a !== null) {
    const step = si + 1 < times.length ? times[si + 1] - times[si]
              : si > 0                 ? times[si] - times[si - 1]
              :                          0
    return time <= times[si] + step / 2 ? a : null
  }
  if (b !== null) {
    const step = times[si + 1] - times[si]
    return time >= times[si + 1] - step / 2 ? b : null
  }
  return null
}

// Readings with no adjacent reading either side. A line series draws a segment
// between neighbours, so these produce nothing at all — a member sampled more
// coarsely than the run's grid is made up entirely of them.
function orphanReadings(t: Trace): [number, number][] {
  const out: [number, number][] = []
  for (let i = 0; i < t.values.length; i++) {
    const v = t.values[i]
    if (v === null) continue
    const hasPrev = i > 0 && t.values[i - 1] !== null
    const hasNext = i < t.values.length - 1 && t.values[i + 1] !== null
    if (!hasPrev && !hasNext) out.push([t.times[i], v])
  }
  return out
}

// Companion series drawing those readings as points. Kept separate so the line
// series keeps `symbol: 'none'`, which is what suppresses the axis-triggered
// hover circles.
function orphanSeries(t: Trace, color: string, z: number): LineSeriesOption[] {
  const data = orphanReadings(t)
  if (!data.length) return []
  return [{
    type: 'line',
    data,
    lineStyle: { width: 0 },
    itemStyle: { color },
    showSymbol: true, symbol: 'circle', symbolSize: 2, silent: true, z,
  }]
}

function buildPlumeSeries(data: ParamData): LineSeriesOption[] {
  const series: LineSeriesOption[] = []
  const pfTraces = data.traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  const boldTraces = data.traces.filter(t => t.member_type === 'cf' || t.member_type === 'fc')
  const analysisTraces = data.traces.filter(t => t.member_type === 'an')

  if (pfTraces.length > 1) {
    // Faint spaghetti
    for (const trace of pfTraces) {
      series.push({
        type: 'line',
        data: trace.times.map((ms, i) => [ms, trace.values[i]]),
        lineStyle: { width: 1, color: 'rgba(70, 120, 220, 0.31)' },
        itemStyle: { color: 'rgba(70, 120, 220, 0)' },
        showSymbol: false, symbol: 'none', silent: true, z: 3,
      })
      series.push(...orphanSeries(trace, 'rgba(70, 120, 220, 0.31)', 3))
    }
  }

  // Analysis line
  for (const trace of analysisTraces) {
    series.push({
      type: 'line',
      data: trace.times.map((ms, i) => [ms, trace.values[i]]),
      lineStyle: { width: 2, color: 'rgba(0, 0, 0, 0.7)' },
      itemStyle: { color: 'transparent' },
      showSymbol: false, silent: true, z: 4,
    })
    series.push(...orphanSeries(trace, 'rgba(0, 0, 0, 0.7)', 4))
  }

  // CF / FC lines
  for (const trace of boldTraces) {
    const isFC = trace.member_type === 'fc'
    const isCF = trace.member_type === 'cf'
    const color = isFC ? 'rgba(0, 0, 0, 0.38)' : isCF ? 'rgba(80, 50, 200, 0.55)' : 'rgba(70, 120, 220, 0.31)'
    series.push({
      type: 'line',
      data: trace.times.map((ms, i) => [ms, trace.values[i]]),
      lineStyle: { width: 1, color },
      itemStyle: { color: 'transparent' },
      showSymbol: false, silent: true, z: 3,
    })
    series.push(...orphanSeries(trace, color, 3))
  }

  return series
}

function marchingSquares(
  ctx: CanvasRenderingContext2D, grid: Float64Array, nX: number, nY: number,
  pxX: number[], pxY: number[],
  bands: [number, string][],
) {
  for (const [minFrac, color] of bands) {
    ctx.fillStyle = color
    ctx.beginPath()
    for (let xi = 0; xi < nX - 1; xi++) {
      for (let yi = 0; yi < nY - 1; yi++) {
        const v00 = grid[xi * nY + yi], v10 = grid[(xi + 1) * nY + yi]
        const v11 = grid[(xi + 1) * nY + (yi + 1)], v01 = grid[xi * nY + (yi + 1)]
        const idx = (v00 >= minFrac ? 1 : 0) | (v10 >= minFrac ? 2 : 0)
                  | (v11 >= minFrac ? 4 : 0) | (v01 >= minFrac ? 8 : 0)
        if (idx === 0) continue
        const x0 = pxX[xi], x1 = pxX[xi + 1], y0 = pxY[yi], y1 = pxY[yi + 1]
        if (idx === 15) { ctx.rect(x0, y1, x1 - x0, y0 - y1); continue }
        const lerp = (a: number, b: number, va: number, vb: number) =>
          a + (minFrac - va) / (vb - va) * (b - a)
        const B: [number, number] = [lerp(x0, x1, v00, v10), y0]
        const R: [number, number] = [x1, lerp(y0, y1, v10, v11)]
        const T: [number, number] = [lerp(x0, x1, v01, v11), y1]
        const L: [number, number] = [x0, lerp(y0, y1, v00, v01)]
        const C00: [number, number] = [x0, y0], C10: [number, number] = [x1, y0]
        const C11: [number, number] = [x1, y1], C01: [number, number] = [x0, y1]
        let polys: [number, number][][]
        switch (idx) {
          case 1:  polys = [[C00, B, L]]; break
          case 2:  polys = [[B, C10, R]]; break
          case 3:  polys = [[C00, C10, R, L]]; break
          case 4:  polys = [[R, C11, T]]; break
          case 5:  { const c = (v00+v10+v11+v01)/4; polys = c >= minFrac ? [[C00,B,R,C11,T,L]] : [[C00,B,L],[R,C11,T]]; break }
          case 6:  polys = [[B, C10, C11, T]]; break
          case 7:  polys = [[C00, C10, C11, T, L]]; break
          case 8:  polys = [[L, T, C01]]; break
          case 9:  polys = [[C00, B, T, C01]]; break
          case 10: { const c = (v00+v10+v11+v01)/4; polys = c >= minFrac ? [[B,C10,R,T,C01,L]] : [[B,C10,R],[L,T,C01]]; break }
          case 11: polys = [[C00, C10, R, T, C01]]; break
          case 12: polys = [[L, R, C11, C01]]; break
          case 13: polys = [[C00, B, R, C11, C01]]; break
          case 14: polys = [[B, C10, C11, C01, L]]; break
          default: polys = []; break
        }
        for (const poly of polys) {
          ctx.moveTo(poly[0][0], poly[0][1])
          for (let k = 1; k < poly.length; k++) ctx.lineTo(poly[k][0], poly[k][1])
          ctx.closePath()
        }
      }
    }
    ctx.fill()
  }
}

// Abramowitz & Stegun normal CDF approximation
function normalCDF(x: number): number {
  if (x < -8) return 0
  if (x > 8) return 1
  const t = 1 / (1 + 0.2316419 * Math.abs(x))
  const d = 0.3989422804014327 * Math.exp(-0.5 * x * x)
  const p = d * t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))))
  return x > 0 ? 1 - p : p
}

function renderAdaptiveContour(canvas: HTMLCanvasElement, chart: ECharts, data: ParamData) {
  const t0 = performance.now()
  const pfTraces = data.traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  if (pfTraces.length < 2) return

  const byTime = new Map<number, number[]>()
  for (const t of pfTraces) {
    for (let i = 0; i < t.times.length; i++) {
      // A member with no value here doesn't count toward this time's spread.
      // Guarding before the entry is created also keeps times where nothing
      // has arrived out of `times`, rather than leaving an empty one behind.
      const v = t.values[i]
      if (v === null) continue
      const ms = t.times[i]
      if (!byTime.has(ms)) byTime.set(ms, [])
      byTime.get(ms)!.push(v)
    }
  }
  const times = [...byTime.keys()].sort((a, b) => a - b)

  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()
  const [, yMax] = chart.convertFromPixel('grid', [Math.round(rect.x), Math.round(rect.y)])
  const [, yMin] = chart.convertFromPixel('grid', [Math.round(rect.x), Math.round(rect.y + rect.height)])

  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()
  ctx.beginPath()
  ctx.rect(Math.round(rect.x), Math.round(rect.y), Math.round(rect.width), Math.round(rect.height))
  ctx.clip()

  const HALF_W = 0.5
  const EVAL_STEP = 0.2
  const nY = Math.ceil((yMax - yMin) / EVAL_STEP) + 1
  const nX = times.length
  const grid = new Float64Array(nX * nY)

  for (let si = 0; si < nX; si++) {
    const sorted = byTime.get(times[si])!.slice().sort((a, b) => a - b)
    const N = sorted.length

    // Silverman pilot bandwidth
    const mean = sorted.reduce((a, b) => a + b, 0) / N
    const variance = sorted.reduce((a, b) => a + (b - mean) ** 2, 0) / N
    const sigma = Math.sqrt(variance)
    const q1 = sorted[Math.floor(N * 0.25)]
    const q3 = sorted[Math.floor(N * 0.75)]
    const iqr = q3 - q1
    const h0 = 0.9 * Math.min(sigma, iqr / 1.34) * Math.pow(N, -0.2)

    // Pilot density → local bandwidths
    const pilot: number[] = []
    const invH0 = 1 / h0
    for (let i = 0; i < N; i++) {
      let d = 0
      for (let j = 0; j < N; j++) {
        d += Math.exp(-0.5 * ((sorted[i] - sorted[j]) * invH0) ** 2)
      }
      pilot.push(d * invH0 * 0.3989422804014327 / N)
    }
    const logMean = pilot.reduce((a, d) => a + Math.log(Math.max(d, 1e-10)), 0) / N
    const geoMean = Math.exp(logMean)
    const localH = pilot.map(d => h0 * Math.pow(Math.max(d, 1e-10) / geoMean, -0.5))

    // Interval probability at each grid point
    for (let yi = 0; yi < nY; yi++) {
      const y = yMin + yi * EVAL_STEP
      let p = 0
      for (let j = 0; j < N; j++) {
        const hj = localH[j]
        p += normalCDF((y + HALF_W - sorted[j]) / hj) - normalCDF((y - HALF_W - sorted[j]) / hj)
      }
      grid[si * nY + yi] = p / N
    }
  }

  const pxX = times.map(t => Math.round(chart.convertToPixel('grid', [t, 0])[0]))
  const pxY: number[] = []
  for (let yi = 0; yi < nY; yi++) {
    pxY.push(Math.round(chart.convertToPixel('grid', [0, yMin + yi * EVAL_STEP])[1]))
  }

  const BANDS: [number, string][] = [
    [0.005, 'rgba(140, 140, 200, 0.15)'],
    [0.06,  'rgba(120, 80, 180, 0.20)'],
    [0.14,  'rgba(160, 50, 120, 0.25)'],
    [0.24,  'rgba(200, 150, 30, 0.30)'],
  ]

  marchingSquares(ctx, grid, nX, nY, pxX, pxY, BANDS)

  ctx.restore()
  console.log(`Adaptive contour: ${(performance.now() - t0).toFixed(0)}ms (${nX} steps)`)
}

function renderDensity(canvas: HTMLCanvasElement, chart: ECharts, data: ParamData) {
  const t0 = performance.now()
  const pfTraces = data.traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  if (pfTraces.length < 2) return

  const byTime = new Map<number, number[]>()
  for (const t of pfTraces) {
    for (let i = 0; i < t.times.length; i++) {
      // A member with no value here doesn't count toward this time's spread.
      // Guarding before the entry is created also keeps times where nothing
      // has arrived out of `times`, rather than leaving an empty one behind.
      const v = t.values[i]
      if (v === null) continue
      const ms = t.times[i]
      if (!byTime.has(ms)) byTime.set(ms, [])
      byTime.get(ms)!.push(v)
    }
  }
  const times = [...byTime.keys()].sort((a, b) => a - b)

  // Get grid pixel bounds from ECharts
  const p1 = chart.convertToPixel('grid', [times[0], 0])
  const p2 = chart.convertToPixel('grid', [times[times.length - 1], 1])
  if (!p1 || !p2) return
  const pxPerUnit = Math.abs(p1[1] - p2[1])  // pixels per data unit

  // Get actual grid pixel bounds and data range from ECharts
  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()
  const gridX = Math.round(rect.x)
  const gridY = Math.round(rect.y)
  const gridW = Math.round(rect.width)
  const gridH = Math.round(rect.height)
  if (gridW <= 0 || gridH <= 0) return

  const [, yMax] = chart.convertFromPixel('grid', [gridX, gridY])
  const [, yMin] = chart.convertFromPixel('grid', [gridX + gridW, gridY + gridH])

  // Clip density to actual data time range
  const xMinData = times[0]
  const xMaxData = times[times.length - 1]
  const dataLeft = Math.round(chart.convertToPixel('grid', [xMinData, 0])[0])
  const dataRight = Math.round(chart.convertToPixel('grid', [xMaxData, 0])[0])
  const dataW = dataRight - dataLeft
  if (gridW <= 0 || gridH <= 0 || dataW <= 0) return

  // Set canvas size
  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const n = pfTraces.length
  const nYBins = 300
  const nTimes = times.length
  const yScale = nYBins / (yMax - yMin)

  // Compute KDE independently at each step
  const stepColumns: Float32Array[] = []
  let maxDensity = 0

  for (let si = 0; si < nTimes; si++) {
    const members = byTime.get(times[si])!
    const sorted = [...members].sort((a, b) => a - b)

    // Silverman bandwidth
    const mean = sorted.reduce((a, b) => a + b, 0) / n
    const sigma = Math.sqrt(sorted.reduce((a, v) => a + (v - mean) ** 2, 0) / n)
    const iqr = sorted[Math.floor(0.75 * (n - 1))] - sorted[Math.floor(0.25 * (n - 1))]
    const h = Math.max(0.9 * Math.min(sigma, iqr / 1.34) * Math.pow(n, -0.2), 0.1)

    const col = new Float32Array(nYBins)
    const invH = 1 / h
    const reach = 4 * h * yScale
    for (let mi = 0; mi < n; mi++) {
      const v = members[mi]
      const center = (yMax - v) * yScale
      const lo = Math.max(Math.floor(center - reach), 0)
      const hi = Math.min(Math.ceil(center + reach), nYBins - 1)
      for (let py = lo; py <= hi; py++) {
        const y = yMax - (py / nYBins) * (yMax - yMin)
        const z = (y - v) * invH
        col[py] += EXP_LUT[Math.round(Math.abs(z) * EXP_RES)] ?? 0
      }
    }
    const norm = 1 / (n * h * 2.5066)
    for (let py = 0; py < nYBins; py++) col[py] *= norm
    const colMax = col.reduce((a, b) => Math.max(a, b), 0)
    if (colMax > maxDensity) maxDensity = colMax
    stepColumns.push(col)
  }

  if (maxDensity === 0) return
  const t1 = performance.now()

  // Render: linearly interpolate density between steps for each pixel column
  const nXPixels = dataW
  const small = ctx.createImageData(nXPixels, nYBins)
  const buf = small.data
  const invMax = 1 / maxDensity

  // Pixel X positions for each step
  const stepPx = times.map(t => Math.round(chart.convertToPixel('grid', [t, 0])[0]) - dataLeft)

  for (let px = 0; px < nXPixels; px++) {
    // Find surrounding steps
    let si = 0
    while (si < nTimes - 1 && stepPx[si + 1] < px) si++

    let col: Float32Array
    if (si >= nTimes - 1 || stepPx[si] >= px) {
      col = stepColumns[si]
    } else {
      // Linearly interpolate density between steps
      const f = (px - stepPx[si]) / (stepPx[si + 1] - stepPx[si])
      col = new Float32Array(nYBins)
      const colA = stepColumns[si], colB = stepColumns[si + 1]
      for (let py = 0; py < nYBins; py++) {
        col[py] = colA[py] * (1 - f) + colB[py] * f
      }
    }

    for (let cy = 0; cy < nYBins; cy++) {
      const d = col[cy] * invMax
      if (d === 0) continue
      plasmaPixel(buf, (cy * nXPixels + px) * 4, d)
    }
  }

  // Render at native X resolution, scale Y only
  const offscreen = new OffscreenCanvas(nXPixels, nYBins)
  const offCtx = offscreen.getContext('2d')!
  offCtx.putImageData(small, 0, 0)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(offscreen, dataLeft, gridY, dataW, gridH)
  const t2 = performance.now()
  console.log(`KDE: compute=${(t1-t0).toFixed(0)}ms render=${(t2-t1).toFixed(0)}ms total=${(t2-t0).toFixed(0)}ms (${nTimes} steps → ${nXPixels}x${nYBins} pixels)`)
}

// Per pin-local day, the ensemble spread of each member's daily maximum (red)
// and daily minimum (blue), each KDE'd and filling that day's full x-band.
function renderDailyExtremes(canvas: HTMLCanvasElement, chart: ECharts, data: ParamData, tz: string) {
  const t0 = performance.now()
  const pfTraces = data.traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  if (pfTraces.length < 2) return

  // Reduce each member to its min and max within each local day, then collect
  // those across members into per-day distributions.
  const dayMax = new Map<number, number[]>()
  const dayMin = new Map<number, number[]>()
  // The span each run declares, taken from its grid endpoints. Steps can be
  // missing anywhere inside it, so there is no contiguous "range of data" to
  // find by scanning — only the span the chart covers, within which any
  // individual day may turn out to have nothing.
  let xMinData = Infinity, xMaxData = -Infinity
  for (const tr of pfTraces) {
    if (!tr.times.length) continue
    if (tr.times[0] < xMinData) xMinData = tr.times[0]
    if (tr.times[tr.times.length - 1] > xMaxData) xMaxData = tr.times[tr.times.length - 1]
  }

  for (const tr of pfTraces) {
    const perDay = new Map<number, { mn: number; mx: number }>()
    for (let i = 0; i < tr.times.length; i++) {
      const v = tr.values[i]
      if (v === null) continue
      const day = dayStart(tr.times[i], tz)
      const cur = perDay.get(day)
      if (!cur) perDay.set(day, { mn: v, mx: v })
      else { if (v < cur.mn) cur.mn = v; if (v > cur.mx) cur.mx = v }
    }
    for (const [day, { mn, mx }] of perDay) {
      if (!dayMax.has(day)) { dayMax.set(day, []); dayMin.set(day, []) }
      dayMax.get(day)!.push(mx)
      dayMin.get(day)!.push(mn)
    }
  }
  const days = [...dayMax.keys()].sort((a, b) => a - b)
  if (!days.length) return

  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()
  const gridX = Math.round(rect.x), gridY = Math.round(rect.y)
  const gridW = Math.round(rect.width), gridH = Math.round(rect.height)
  if (gridW <= 0 || gridH <= 0) return

  const [, yMax] = chart.convertFromPixel('grid', [gridX, gridY])
  const [, yMin] = chart.convertFromPixel('grid', [gridX + gridW, gridY + gridH])

  const dataLeft = Math.round(chart.convertToPixel('grid', [xMinData, 0])[0])
  const dataRight = Math.round(chart.convertToPixel('grid', [xMaxData, 0])[0])
  const dataW = dataRight - dataLeft
  if (dataW <= 0) return

  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const nYBins = 300
  const yScale = nYBins / (yMax - yMin)

  // Silverman-bandwidth gaussian KDE of one distribution into a y-column.
  const kdeColumn = (vals: number[]): Float32Array => {
    const col = new Float32Array(nYBins)
    const n = vals.length
    if (n < 2) return col
    const sorted = [...vals].sort((a, b) => a - b)
    const mean = sorted.reduce((a, b) => a + b, 0) / n
    const sigma = Math.sqrt(sorted.reduce((a, v) => a + (v - mean) ** 2, 0) / n)
    const iqr = sorted[Math.floor(0.75 * (n - 1))] - sorted[Math.floor(0.25 * (n - 1))]
    const h = Math.max(0.9 * Math.min(sigma, iqr / 1.34) * Math.pow(n, -0.2), 0.1)
    const invH = 1 / h
    const reach = 4 * h * yScale
    for (const v of vals) {
      const center = (yMax - v) * yScale
      const lo = Math.max(Math.floor(center - reach), 0)
      const hi = Math.min(Math.ceil(center + reach), nYBins - 1)
      for (let py = lo; py <= hi; py++) {
        const y = yMax - (py / nYBins) * (yMax - yMin)
        const z = (y - v) * invH
        col[py] += EXP_LUT[Math.round(Math.abs(z) * EXP_RES)] ?? 0
      }
    }
    // Normalise each distribution to its own peak so every day reads at full
    // intensity regardless of how tight or broad its spread is.
    let peak = 0
    for (let py = 0; py < nYBins; py++) if (col[py] > peak) peak = col[py]
    if (peak > 0) for (let py = 0; py < nYBins; py++) col[py] /= peak
    return col
  }

  const maxCols = new Map<number, Float32Array>()
  const minCols = new Map<number, Float32Array>()
  for (const day of days) {
    maxCols.set(day, kdeColumn(dayMax.get(day)!))
    minCols.set(day, kdeColumn(dayMin.get(day)!))
  }

  const small = ctx.createImageData(dataW, nYBins)
  const buf = small.data
  const PEAK_ALPHA = 0.5

  // Pure red = max distribution, pure blue = min distribution: orthogonal
  // channels combined by an order-independent premultiplied union, so neither
  // masks the other and overlap reads as magenta.
  for (const day of days) {
    const loT = Math.max(day, xMinData)
    const hiT = Math.min(day + 86400000, xMaxData)
    if (hiT <= loT) continue
    const loPx = Math.max(Math.round(chart.convertToPixel('grid', [loT, 0])[0]) - dataLeft, 0)
    const hiPx = Math.min(Math.round(chart.convertToPixel('grid', [hiT, 0])[0]) - dataLeft, dataW)
    const mc = maxCols.get(day)!, nc = minCols.get(day)!
    for (let px = loPx; px < hiPx; px++) {
      for (let py = 0; py < nYBins; py++) {
        const am = mc[py] * PEAK_ALPHA
        const an = nc[py] * PEAK_ALPHA
        const a = am + an - am * an
        if (a <= 0) continue
        const idx = (py * dataW + px) * 4
        buf[idx] = 255 * am / a
        buf[idx + 2] = 255 * an / a
        buf[idx + 3] = 255 * a
      }
    }
  }

  const offscreen = new OffscreenCanvas(dataW, nYBins)
  offscreen.getContext('2d')!.putImageData(small, 0, 0)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(offscreen, dataLeft, gridY, dataW, gridH)
  console.log(`Daily min/max: ${(performance.now() - t0).toFixed(0)}ms (${days.length} days)`)
}

function renderContour(canvas: HTMLCanvasElement, chart: ECharts, data: ParamData) {
  const t0 = performance.now()
  const pfTraces = data.traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  if (pfTraces.length < 2) return

  const byTime = new Map<number, number[]>()
  for (const t of pfTraces) {
    for (let i = 0; i < t.times.length; i++) {
      // A member with no value here doesn't count toward this time's spread.
      // Guarding before the entry is created also keeps times where nothing
      // has arrived out of `times`, rather than leaving an empty one behind.
      const v = t.values[i]
      if (v === null) continue
      const ms = t.times[i]
      if (!byTime.has(ms)) byTime.set(ms, [])
      byTime.get(ms)!.push(v)
    }
  }
  const times = [...byTime.keys()].sort((a, b) => a - b)

  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()

  const [, yMax] = chart.convertFromPixel('grid', [Math.round(rect.x), Math.round(rect.y)])
  const [, yMin] = chart.convertFromPixel('grid', [Math.round(rect.x), Math.round(rect.y + rect.height)])

  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()
  ctx.beginPath()
  ctx.rect(Math.round(rect.x), Math.round(rect.y), Math.round(rect.width), Math.round(rect.height))
  ctx.clip()

  // Build 2D grid: fixed 1K bins
  const EVAL_STEP = 0.2
  const nY = Math.ceil((yMax - yMin) / EVAL_STEP) + 1
  const nX = times.length
  const grid = new Float64Array(nX * nY)

  for (let si = 0; si < nX; si++) {
    const members = byTime.get(times[si])!
    const N = members.length
    const bins = new Map<number, number>()
    for (const m of members) {
      const bin = Math.floor(m)
      bins.set(bin, (bins.get(bin) ?? 0) + 1)
    }
    for (let yi = 0; yi < nY; yi++) {
      const y = yMin + yi * EVAL_STEP
      grid[si * nY + yi] = (bins.get(Math.floor(y)) ?? 0) / N
    }
  }

  // Light 2D gaussian blur
  const BLUR_R = 2
  const kernel: number[] = []
  for (let i = -BLUR_R; i <= BLUR_R; i++) kernel.push(Math.exp(-0.5 * (i / 1.0) ** 2))
  const kSum = kernel.reduce((a, b) => a + b)
  const tmp = new Float64Array(nX * nY)
  for (let yi = 0; yi < nY; yi++) {
    for (let xi = 0; xi < nX; xi++) {
      let s = 0
      for (let k = -BLUR_R; k <= BLUR_R; k++) {
        s += grid[Math.min(Math.max(xi + k, 0), nX - 1) * nY + yi] * kernel[k + BLUR_R]
      }
      tmp[xi * nY + yi] = s / kSum
    }
  }
  const contourGrid = new Float64Array(nX * nY)
  for (let xi = 0; xi < nX; xi++) {
    for (let yi = 0; yi < nY; yi++) {
      let s = 0
      for (let k = -BLUR_R; k <= BLUR_R; k++) {
        s += tmp[xi * nY + Math.min(Math.max(yi + k, 0), nY - 1)] * kernel[k + BLUR_R]
      }
      contourGrid[xi * nY + yi] = s / kSum
    }
  }

  // Pixel coordinates
  const pxX = times.map(t => Math.round(chart.convertToPixel('grid', [t, 0])[0]))
  const pxY: number[] = []
  for (let yi = 0; yi < nY; yi++) {
    pxY.push(Math.round(chart.convertToPixel('grid', [0, yMin + yi * EVAL_STEP])[1]))
  }

  const BANDS: [number, string][] = [
    [0.005, 'rgba(140, 140, 200, 0.15)'],
    [0.06,  'rgba(120, 80, 180, 0.20)'],
    [0.14,  'rgba(160, 50, 120, 0.25)'],
    [0.24,  'rgba(200, 150, 30, 0.30)'],
  ]

  marchingSquares(ctx, contourGrid, nX, nY, pxX, pxY, BANDS)

  ctx.restore()
  console.log(`Contour: ${(performance.now() - t0).toFixed(0)}ms (${nX} steps)`)
}

function buildFilledPlumeSeries(
  allData: Record<string, ParamData>,
  compareAllData: Record<string, ParamData> | null,
  cp: { top: string; bot: string },
): LineSeriesOption[] {
  const datasets: ParamData[] = []
  for (const key of [cp.top, cp.bot]) {
    if (allData[key]) datasets.push(allData[key])
    if (compareAllData?.[key]) datasets.push(compareAllData[key])
  }
  return datasets.flatMap(d => invisibleSeries(d))
}

function renderFilledPlume(canvas: HTMLCanvasElement, chart: ECharts, allData: Record<string, ParamData>, topKey: string, botKey: string) {
  const t0 = performance.now()
  const tempData = allData[topKey]
  const dewData = allData[botKey]
  if (!tempData || !dewData) return

  // Match traces by (run_id, member_type, member)
  const dewMap = new Map<string, Trace>()
  for (const t of dewData.traces) {
    dewMap.set(`${t.run_id}:${t.member_type}:${t.member}`, t)
  }

  const pairs: { temp: Trace; dew: Trace; weight: number }[] = []
  for (const t of tempData.traces) {
    if (t.member_type === '_anchor' || t.member_type === 'an') continue
    const key = `${t.run_id}:${t.member_type}:${t.member}`
    const d = dewMap.get(key)
    if (!d) continue
    const isCF = t.member_type === 'cf'
    const isFC = t.member_type === 'fc'
    pairs.push({ temp: t, dew: d, weight: isCF ? 1.5 : isFC ? 1.0 : 1.0 })
  }
  if (pairs.length === 0) return

  // Get grid bounds from ECharts
  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()
  const gridX = Math.round(rect.x)
  const gridY = Math.round(rect.y)
  const gridW = Math.round(rect.width)
  const gridH = Math.round(rect.height)
  if (gridW <= 0 || gridH <= 0) return

  const [, yMax] = chart.convertFromPixel('grid', [gridX, gridY])
  const [, yMin] = chart.convertFromPixel('grid', [gridX + gridW, gridY + gridH])

  // Determine x range from the union of all pairs -- fc pairs end at the
  // deterministic horizon, pf pairs run further out into the ensemble
  // tail. Clipping to pairs[0] would erase the pf-only tail from the
  // canvas even though those values are in the traces.
  let xMinData = Infinity, xMaxData = -Infinity
  for (const { temp } of pairs) {
    if (temp.times.length === 0) continue
    if (temp.times[0] < xMinData) xMinData = temp.times[0]
    if (temp.times[temp.times.length - 1] > xMaxData) xMaxData = temp.times[temp.times.length - 1]
  }
  if (xMaxData === -Infinity) return
  const dataLeft = Math.round(chart.convertToPixel('grid', [xMinData, 0])[0])
  const dataRight = Math.round(chart.convertToPixel('grid', [xMaxData, 0])[0])
  const dataW = dataRight - dataLeft

  // Additive accumulation buffer at native canvas resolution
  const nX = dataW
  const nY = gridH
  if (nX <= 0 || nY <= 0) return
  const buf = new Float32Array(nX * nY)

  // Per-column weight of the members that actually contribute to that column,
  // tallied as they are drawn. Normalising each column by its own local total —
  // rather than by a single global member count — keeps the fill density
  // consistent as the member count falls off toward the long-lead tail, where
  // the older cycles drop out at staggered horizons.
  //
  // The tally has to come from the members that really painted, not from their
  // declared time spans: a span covers the run's whole grid, including steps
  // that haven't arrived yet, so counting spans would leave a partly-loaded
  // column permanently short of full density.
  const colWeight = new Float32Array(nX)

  for (const { temp, dew, weight } of pairs) {
    let si = 0

    for (let xi = 0; xi < nX; xi++) {
      const dataTime = xMinData + (xi / (nX - 1)) * (xMaxData - xMinData)

      while (si < temp.times.length - 1 && temp.times[si + 1] < dataTime) si++

      if (dataTime < temp.times[0] || dataTime > temp.times[temp.times.length - 1]) continue

      // temp and dew share their run's grid, so one cursor indexes both.
      const tempVal = sampleAt(temp.times, temp.values, si, dataTime)
      const dewVal = sampleAt(temp.times, dew.values, si, dataTime)
      // The band needs both of its edges, so a gap in either leaves this column
      // unpainted rather than bridged from across the gap.
      if (tempVal === null || dewVal === null) continue

      colWeight[xi] += weight

      // Map to y-bins and fill
      const yTop = Math.max(Math.floor((yMax - tempVal) / (yMax - yMin) * nY), 0)
      const yBot = Math.min(Math.ceil((yMax - dewVal) / (yMax - yMin) * nY), nY - 1)
      for (let yi = yTop; yi <= yBot; yi++) {
        buf[yi * nX + xi] += weight
      }
    }
  }

  // Now that each column's contributors are known, scale it to them, so a
  // column reaches full density wherever every member that reported there
  // overlaps — however many of them there happen to be.
  for (let xi = 0; xi < nX; xi++) {
    const cw = colWeight[xi]
    if (cw === 0) continue
    for (let yi = 0; yi < nY; yi++) buf[yi * nX + xi] /= cw
  }

  // Render directly at canvas resolution — no scaling, no smoothing
  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const img = ctx.createImageData(nX, nY)
  const pixels = img.data

  for (let i = 0; i < nX * nY; i++) {
    if (buf[i] === 0) continue
    plasmaPixel(pixels, i * 4, buf[i])
  }

  ctx.putImageData(img, dataLeft, gridY)

  const toY = (v: number) => gridY + (yMax - v) / (yMax - yMin) * gridH

  // Draw FC as two thin lines (2t and 2d) on top of density
  const fcPairs = pairs.filter(p => p.temp.member_type === 'fc')
  for (const { temp, dew } of fcPairs) {
    for (const trace of [temp, dew]) {
      ctx.beginPath()
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)'
      ctx.lineWidth = 0.5
      let started = false
      for (let i = 0; i < trace.times.length; i++) {
        // A gap breaks the line: the next reading begins a fresh segment
        // rather than being joined straight across the missing steps.
        const v = trace.values[i]
        if (v === null) { started = false; continue }
        const x = chart.convertToPixel('grid', [trace.times[i], 0])[0]
        const y = toY(v)
        if (!started) { ctx.moveTo(x, y); started = true } else ctx.lineTo(x, y)
      }
      ctx.stroke()
    }
  }

  // Draw analysis as bold black lines (2t and 2d)
  for (const src of [tempData, dewData]) {
    const anTraces = src.traces.filter(t => t.member_type === 'an')
    for (const trace of anTraces) {
      ctx.beginPath()
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)'
      ctx.lineWidth = 2
      let started = false
      for (let i = 0; i < trace.times.length; i++) {
        // A gap breaks the line: the next reading begins a fresh segment
        // rather than being joined straight across the missing steps.
        const v = trace.values[i]
        if (v === null) { started = false; continue }
        const x = chart.convertToPixel('grid', [trace.times[i], 0])[0]
        const y = toY(v)
        if (!started) { ctx.moveTo(x, y); started = true } else ctx.lineTo(x, y)
      }
      ctx.stroke()
    }
  }

  const t1 = performance.now()
  console.log(`Meteogram: ${(t1 - t0).toFixed(0)}ms (${nX}x${nY} pixels)`)
}


function renderMemberStrip(
  canvas: HTMLCanvasElement, chart: ECharts,
  traces: Trace[],
  xMinData: number, xMaxData: number, dataLeft: number, dataW: number,
  scale: [number, number, number, number][],
  stamping: Stamping,
) {
  if (!traces.length) return

  const dpr = window.devicePixelRatio || 1
  const nX = Math.round(dataW * dpr)

  // Separate FC (top, 4px) from ensemble members (1px each)
  const fcTraces = traces.filter(t => t.member_type === 'fc')
  const ensTraces = traces.filter(t => t.member_type !== 'fc' && t.member_type !== 'an')

  const FC_CSS_PX = 4
  const ENS_CSS_PX = ensTraces.length < 25 ? 2 : 1
  const fcRows = Math.round(FC_CSS_PX * dpr)
  const ensRowsPer = Math.round(ENS_CSS_PX * dpr)
  const totalCssH = (fcTraces.length ? FC_CSS_PX : 0) + ensTraces.length * ENS_CSS_PX
  const nY = (fcTraces.length ? fcRows : 0) + ensTraces.length * ensRowsPer

  if (nX <= 0 || nY <= 0) return
  canvas.width = Math.round(canvas.offsetWidth * dpr)
  canvas.height = nY
  canvas.style.height = `${totalCssH}px`
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const img = ctx.createImageData(nX, nY)
  const pixels = img.data

  // Sort ensemble by weighted-average value — 50% of weight in first 48h (exponential decay)
  const lambda = Math.LN2 / 48  // half-life = 48 hours
  const sorted = ensTraces.map(t => {
    const t0 = t.times[0]
    let wSum = 0, vSum = 0
    for (let i = 0; i < t.values.length; i++) {
      // Times this member has no reading for don't pull its average around.
      const v = t.values[i]
      if (v === null) continue
      const hoursFromStart = (t.times[i] - t0) / 3600_000
      const w = Math.exp(-lambda * hoursFromStart)
      wSum += w
      vSum += v * w
    }
    // A member with no readings at all has no position to sort by; pin it to a
    // fixed key so the row order stays stable rather than going arbitrary on NaN.
    return { trace: t, sortKey: wSum > 0 ? vSum / wSum : 0 }
  }).sort((a, b) => a.sortKey - b.sortKey)

  // Insert FC into sorted position based on same weighted average
  const allSorted: { trace: Trace; rows: number; sortKey: number }[] =
    sorted.map(s => ({ ...s, rows: ensRowsPer }))
  for (const t of fcTraces) {
    const t0 = t.times[0]
    let wSum = 0, vSum = 0
    for (let i = 0; i < t.values.length; i++) {
      // Times this member has no reading for don't pull its average around.
      const v = t.values[i]
      if (v === null) continue
      const hoursFromStart = (t.times[i] - t0) / 3600_000
      const w = Math.exp(-lambda * hoursFromStart)
      wSum += w
      vSum += v * w
    }
    allSorted.push({ trace: t, rows: fcRows, sortKey: wSum > 0 ? vSum / wSum : 0 })
  }
  allSorted.sort((a, b) => a.sortKey - b.sortKey)

  const ordered: { trace: Trace; yStart: number; rows: number }[] = []
  let yOff = 0
  for (const { trace, rows } of allSorted) {
    ordered.push({ trace, yStart: yOff, rows })
    yOff += rows
  }

  for (const { trace: t, yStart, rows } of ordered) {
    let si = 0

    for (let xi = 0; xi < nX; xi++) {
      const dataTime = xMinData + (xi / (nX - 1)) * (xMaxData - xMinData)

      while (si < t.times.length - 1 && t.times[si + 1] < dataTime) si++
      if (dataTime < t.times[0] || dataTime > t.times[t.times.length - 1]) continue

      // An interval-stamped reading is filed under the end of the interval it
      // covers, so the value governing (times[si], times[si+1]] is the one at
      // si+1. It holds flat across that interval, so no half-step treatment.
      const val = stamping === 'interval-end'
        ? t.values[Math.min(si + 1, t.values.length - 1)]
        : sampleAt(t.times, t.values, si, dataTime)
      // No reading, no colour: leave the cell transparent rather than borrowing
      // whatever the step on the far side of the gap happens to say.
      if (val === null) continue

      const [cr, cg, cb, ca] = scaleColor(val, scale)
      for (let r = 0; r < rows && yStart + r < nY; r++) {
        const idx = ((yStart + r) * nX + xi) * 4
        pixels[idx] = cr; pixels[idx + 1] = cg; pixels[idx + 2] = cb; pixels[idx + 3] = ca
      }
    }
    si = 0
  }

  ctx.putImageData(img, Math.round(dataLeft * dpr), 0)
}

function renderPrecip(canvas: HTMLCanvasElement, chart: ECharts, data: ParamData) {
  const t0 = performance.now()
  if (!data.traces.length) return

  const pfTraces = data.traces.filter(t => t.member_type === 'pf' || t.member_type === 'ef')
  const fcTraces = data.traces.filter(t => t.member_type === 'fc' || t.member_type === 'cf')
  const pfCount = pfTraces.length
  if (pfCount === 0 && fcTraces.length === 0) return

  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()
  const gridX = Math.round(rect.x)
  const gridY = Math.round(rect.y)
  const gridW = Math.round(rect.width)
  const gridH = Math.round(rect.height)
  if (gridW <= 0 || gridH <= 0) return

  const [, yMax] = chart.convertFromPixel('grid', [gridX, gridY])
  const [, yMin] = chart.convertFromPixel('grid', [gridX + gridW, gridY + gridH])

  const allTraces = [...pfTraces, ...fcTraces]

  // Find x range across all traces
  let xMinData = Infinity, xMaxData = -Infinity
  for (const t of allTraces) {
    if (t.times[0] < xMinData) xMinData = t.times[0]
    if (t.times[t.times.length - 1] > xMaxData) xMaxData = t.times[t.times.length - 1]
  }
  const dataLeft = Math.round(chart.convertToPixel('grid', [xMinData, 0])[0])
  const dataRight = Math.round(chart.convertToPixel('grid', [xMaxData, 0])[0])
  const dataW = dataRight - dataLeft
  if (dataW <= 0 || gridH <= 0) return

  const nX = dataW
  const nY = gridH
  const buf = new Float32Array(nX * nY)    // ensemble
  const fcBuf = new Float32Array(nX * nY)  // FC/CF

  // Per-column count of the ensemble members that reported at that column, so
  // each column's fill is normalized by its own members rather than the global
  // count — keeps the precip fill density consistent as the time-lagged cycles
  // drop out at staggered horizons toward the tail.
  //
  // Counted from readings that exist, tallied as the members are walked. Not
  // from their declared spans, which cover the run's whole grid including steps
  // that haven't arrived; and not from what actually painted either, since a
  // member reporting no rain still has to count toward the total.
  const colPf = new Float32Array(nX)

  for (const trace of allTraces) {
    const isFC = trace.member_type === 'fc' || trace.member_type === 'cf'
    const target = isFC ? fcBuf : buf

    const vals = trace.values
    let si = 0

    for (let xi = 0; xi < nX; xi++) {
      const dataTime = xMinData + (xi / (nX - 1)) * (xMaxData - xMinData)

      while (si < trace.times.length - 1 && trace.times[si + 1] < dataTime) si++
      if (dataTime < trace.times[0] || dataTime > trace.times[trace.times.length - 1]) continue

      // A running total is a reading at its own instant and interpolates. A rate
      // is the mean over the interval ending at its instant, so the value
      // covering (times[si], times[si+1]] is the one filed at si+1, held flat.
      const val = data.stamping === 'interval-end'
        ? vals[Math.min(si + 1, vals.length - 1)]
        : sampleAt(trace.times, vals, si, dataTime)

      // A missing reading is not the same as no rain: it counts toward nothing,
      // where a reported zero counts toward the column's total but draws no bar.
      if (val === null) continue
      if (!isFC) colPf[xi] += 1
      if (val <= 0) continue

      // Fill from 0 up to val
      const yTop = Math.max(Math.floor((yMax - val) / (yMax - yMin) * nY), 0)
      const yBot = Math.min(Math.ceil((yMax - 0) / (yMax - yMin) * nY), nY - 1)
      for (let yi = yTop; yi <= yBot; yi++) {
        target[yi * nX + xi] += 1
      }
    }
    si = 0
  }

  // Scale each column to the members that reported there. fc/cf count as one
  // member-equivalent against the ensemble, so both buffers take the same
  // divisor; the guard covers a column with no ensemble member at all.
  for (let xi = 0; xi < nX; xi++) {
    const n = Math.max(colPf[xi], 1)
    for (let yi = 0; yi < nY; yi++) {
      const i = yi * nX + xi
      buf[i] /= n
      fcBuf[i] /= n
    }
  }

  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const img = ctx.createImageData(nX, nY)
  const pixels = img.data

  for (let i = 0; i < nX * nY; i++) {
    const d = buf[i] + fcBuf[i]
    if (d === 0) continue
    plasmaPixel(pixels, i * 4, d)
  }

  ctx.putImageData(img, dataLeft, gridY)
  console.log(`Precip: ${(performance.now() - t0).toFixed(0)}ms (${nX}x${nY} pixels)`)
}

function applyWindUnit(data: ParamData, windUnit: WindUnit): ParamData {
  // Wind-speed params (``10si``, ``ws@<L>hPa``) arrive from the backend
  // in m/s; the chart's wind-unit selector picks a display unit, and
  // this transform scales the values + relabels the unit string. No-op
  // when the selected unit is m/s.
  const factor = WIND_UNITS.find(u => u.key === windUnit)?.factor ?? 1
  if (factor === 1 && data.unit === windUnit) return data
  return {
    ...data, unit: windUnit,
    // Scaling has to preserve absence: `null * factor` is 0, which would turn a
    // missing reading into a real calm.
    traces: data.traces.map(t => ({ ...t, values: t.values.map(v => v === null ? null : v * factor) })),
  }
}

function renderStripCanvas(
  strip: Strip,
  canvas: HTMLCanvasElement,
  chart: ECharts,
  allData: Record<string, ParamData>,
  xMin: number, xMax: number, dataLeft: number, dataW: number,
) {
  if (strip.kind === 'wind-rose') {
    renderWindRoses(canvas, chart, allData, strip.u, strip.v)
    return
  }
  // scalar strip — dispatch on the colour ramp. Wind ramps convert
  // m/s → km/h (the scale's native unit) regardless of the centerpiece's
  // wind-unit selection: strip scales are display-internal and the
  // selector only governs the main plume.
  const pd = allData[strip.param]
  if (!pd) { canvas.width = 0; canvas.height = 0; return }
  const scale = strip.ramp === 'precip'   ? PRECIP_SCALE
              : strip.ramp === 'cloud'    ? CLOUD_SCALE
              : strip.ramp === 'moisture' ? MOISTURE_SCALE
              : strip.ramp === 'wind-pl'  ? PL_WIND_SCALE
              :                             WIND_SCALE
  const KMH_FACTOR = 3.6
  const transform = (strip.ramp === 'wind' || strip.ramp === 'wind-pl')
    ? (t: Trace) => t.values.map(v => v === null ? null : v * KMH_FACTOR)
    : (t: Trace) => t.values
  const realTraces = pd.traces.filter(t => t.member_type !== '_anchor' && t.member_type !== 'an')
                              .map(t => ({ ...t, values: transform(t) }))
  // How a reading is stamped is a property of the param, not of the colour
  // ramp it happens to be drawn with.
  renderMemberStrip(canvas, chart, realTraces, xMin, xMax, dataLeft, dataW,
                    scale, pd.stamping)
}

function renderWindRoses(canvas: HTMLCanvasElement, chart: ECharts, allData: Record<string, ParamData>, uKey: string, vKey: string) {
  const uData = allData[uKey], vData = allData[vKey]
  if (!uData || !vData) return

  // All non-anchor traces contribute to the rose — including the
  // deterministic ``cf``/``fc`` trace. Early in a run when only the
  // control member has landed, the rose shows one direction at full
  // intensity; as ensemble members arrive sectors fill in. This
  // degrades gracefully rather than blanking out below the chart.
  const wantedU = uData.traces.filter(t => t.member_type !== '_anchor' && t.member_type !== 'an')
  const wantedV = vData.traces.filter(t => t.member_type !== '_anchor' && t.member_type !== 'an')
  if (!wantedU.length) return

  const byTime = new Map<number, { u: number; v: number }[]>()

  const vMap = new Map<string, Trace>()
  for (const t of wantedV) vMap.set(`${t.run_id}:${t.member_type}:${t.member}`, t)

  for (const ut of wantedU) {
    const vt = vMap.get(`${ut.run_id}:${ut.member_type}:${ut.member}`)
    if (!vt) continue
    // Matched on run, so u and v share one grid by reference: index i is the
    // same instant in both. A rose needs both components, so a gap in either
    // drops that instant rather than pairing it with the wrong one.
    for (let i = 0; i < ut.times.length; i++) {
      const u = ut.values[i], v = vt.values[i]
      if (u === null || v === null) continue
      const ms = ut.times[i]
      if (!byTime.has(ms)) byTime.set(ms, [])
      byTime.get(ms)!.push({ u, v })
    }
  }

  const times = [...byTime.keys()].sort((a, b) => a - b)
  if (!times.length) return

  const coordSys = (chart as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', 0)?.coordinateSystem
  if (!coordSys) return
  const rect = coordSys.getRect()

  // Pin the canvas to the 50-px row reservation that ``stripHeights``
  // accounts for. Without this it picks up the HTML canvas-default
  // 150px and overlaps whatever strip sits below.
  canvas.style.height = '50px'
  canvas.width = canvas.offsetWidth
  canvas.height = canvas.offsetHeight
  const ctx = canvas.getContext('2d')!
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  const SECTORS = 8
  const MIN_ROSE_R = 10
  const MAX_ROSE_R = Math.min(canvas.height / 2 - 2, 20)

  // Find the minimum time interval that gives enough pixel space for roses
  const pxPerHour = Math.abs(
    chart.convertToPixel('grid', [times[0] + 3600_000, 0])[0] -
    chart.convertToPixel('grid', [times[0], 0])[0]
  )

  let intervalMs = times.length > 1 ? times[1] - times[0] : 3600_000
  while ((intervalMs / 3600_000) * pxPerHour < 2 * MIN_ROSE_R + 2) intervalMs *= 2

  // Filter times to this interval
  const filteredTimes = times.filter(ms => (ms - times[0]) % intervalMs === 0)
  const roseGapPx = (intervalMs / 3600_000) * pxPerHour
  const roseR = Math.min(MAX_ROSE_R, roseGapPx / 2 - 1)

  for (const time of filteredTimes) {
    const members = byTime.get(time)!
    const cx = chart.convertToPixel('grid', [time, 0])[0]
    const cy = canvas.height / 2

    // Bin directions into sectors
    const bins = new Float64Array(SECTORS)
    for (const { u, v } of members) {
      // Met direction: where wind comes FROM
      const dir = (Math.atan2(-u, -v) * 180 / Math.PI + 360) % 360
      const sector = Math.round(dir / (360 / SECTORS)) % SECTORS
      bins[sector]++
    }
    const maxBin = Math.max(...bins)
    if (maxBin === 0) continue

    // Draw rose wedges
    const sectorAngle = (2 * Math.PI) / SECTORS
    for (let s = 0; s < SECTORS; s++) {
      if (bins[s] === 0) continue
      const frac = bins[s] / maxBin
      const r = frac * roseR
      // Sector s: 0=N, 1=NE, 2=E, etc. Angle from north, clockwise
      // Canvas: 0 = right, clockwise. North = -π/2
      const angle = s * sectorAngle - Math.PI / 2
      const a0 = angle - sectorAngle / 2
      const a1 = angle + sectorAngle / 2

      ctx.beginPath()
      ctx.moveTo(cx, cy)
      ctx.arc(cx, cy, r, a0, a1)
      ctx.closePath()

      const alpha = 0.3 + 0.5 * frac
      ctx.fillStyle = `rgba(180, 140, 50, ${alpha})`
      ctx.fill()
    }

    // Tiny center dot
    ctx.beginPath()
    ctx.arc(cx, cy, 1.5, 0, 2 * Math.PI)
    ctx.fillStyle = 'rgba(0,0,0,0.3)'
    ctx.fill()
  }
}

export function Chart({ data, allData, config, shading, windUnit, precipMode, compareData, compareAllData }: {
  data: ParamData; allData: Record<string, ParamData>; config: ParamConfig
  shading: 'off' | 'contour' | 'kde' | 'adaptive' | 'daily'; windUnit: WindUnit; precipMode: PrecipMode
  compareData: ParamData | null; compareAllData: Record<string, ParamData> | null
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const densityRef = useRef<HTMLCanvasElement>(null)
  const roseRef = useRef<HTMLCanvasElement>(null)
  const stripRefs = useRef<(HTMLCanvasElement | null)[]>([])
  const chartRef = useRef<ECharts | null>(null)
  const [resizeKey, setResizeKey] = useState(0)

  useEffect(() => {
    if (!containerRef.current) return
    const chart = init(containerRef.current)
    chart.group = 'ecmwf'
    chartRef.current = chart

    const ro = new ResizeObserver(() => { chart.resize(); setResizeKey(k => k + 1) })
    ro.observe(containerRef.current)
    return () => {
      ro.disconnect()
      chart.dispose()
    }
  }, [])

  useEffect(() => {
    if (!chartRef.current || !data.traces.length) return

    const isFilledPlume = config.centerpiece.kind === 'filled-plume'
    const isPrecip = config.centerpiece.kind === 'precip'
    const isPlume = config.centerpiece.kind === 'plume'
    const isWindSpeedPlume = config.centerpiece.kind === 'plume' && /^(10si|ws@\d+hPa)$/.test(config.centerpiece.param)
    // Roses still render below the chart for tabs whose only strip is a
    // wind-rose. Inline-rose-as-strip lands with the synoptic page.
    const hasRoseStrip = config.strips.some(s => s.kind === 'wind-rose')

    // Wind-speed centerpiece applies the user's display-unit factor to
    // the (m/s-native) values; everything else is pass-through.
    const chartData = isWindSpeedPlume ? applyWindUnit(data, windUnit) : data

    // Compute x extent from all traces including anchors
    let xMin = Infinity, xMax = -Infinity
    const traceSets = [chartData.traces]
    if (compareData) traceSets.push(compareData.traces)
    for (const traces of traceSets) {
      for (const t of traces) {
        for (const ms of t.times) {
          if (ms < xMin) xMin = ms
          if (ms > xMax) xMax = ms
        }
      }
    }
    const nights = nightBands(xMin, xMax, chartData.lat, chartData.lon)

    // The chart reads in the pinned location's local time: axis labels and the
    // tooltip localise here, while all data stays true-UTC epoch ms.
    const pinTz = tzlookup(chartData.lat, chartData.lon)
    const dayTicks = localMidnights(xMin, xMax, pinTz)
    const dayFmt = new Intl.DateTimeFormat(undefined, {
      timeZone: pinTz, weekday: 'short', day: 'numeric', month: 'short',
    })
    const tooltipFmt = new Intl.DateTimeFormat(undefined, {
      timeZone: pinTz, weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    })

    const series: LineSeriesOption[] = config.centerpiece.kind === 'filled-plume'
      ? buildFilledPlumeSeries(allData, compareAllData, config.centerpiece)
      : isPrecip
      ? invisibleSeries(chartData)
      : buildPlumeSeries(chartData)

    // Invisible compare series for axis scaling when toggling
    if (compareData && !isFilledPlume) {
      series.push(...invisibleSeries(compareData))
    }

    // Anchor series for full x-axis range (from SSE init metadata)
    const anchorTraces = chartData.traces.filter(t => t.member_type === '_anchor')
    if (anchorTraces.length) {
      series.push(...invisibleSeries({ ...chartData, traces: anchorTraces }))
    }

    // Invisible series for night shading
    if (nights.length) {
      const nightMarkArea = {
        silent: true,
        itemStyle: { color: 'rgba(0, 0, 50, 0.05)' },
        data: nights.map(([a, b]) => [{ xAxis: a }, { xAxis: b }]),
      }
      series.push({ type: 'line', data: [], markArea: nightMarkArea } as LineSeriesOption)
      // Night shading replicated into each strip grid.
      if (isFilledPlume) {
        for (let i = 0; i < config.strips.length; i++) {
          series.push({ type: 'line', data: [], xAxisIndex: i + 1, yAxisIndex: i + 1, markArea: nightMarkArea } as LineSeriesOption)
        }
      }
    }

    // Multi-grid layout: one grid per strip below the centerpiece.
    // Strip heights scale with the trace count of the strip's source param.
    // Wind-rose strips reserve a fixed ~50px row; the rose canvas paints
    // absolute over that slot.
    const stripHeights: number[] = []
    if (isFilledPlume) {
      const stripH = (traces: Trace[]) => {
        const real = traces.filter(t => t.member_type !== '_anchor' && t.member_type !== 'an')
        const fcN = real.filter(t => t.member_type === 'fc' || t.member_type === 'cf').length
        const ensN = real.filter(t => t.member_type !== 'fc' && t.member_type !== 'cf').length
        return (fcN ? 4 : 0) + ensN * (ensN < 25 ? 2 : 1)
      }
      for (const strip of config.strips) {
        if (strip.kind === 'wind-rose') {
          stripHeights.push(50)
          continue
        }
        let h = 0
        for (const src of [allData, compareAllData]) {
          if (!src) continue
          const t = src[strip.param]?.traces
          if (t?.length) h = Math.max(h, stripH(t))
        }
        stripHeights.push(h)
      }
      // Invisible series on each strip grid for x-axis range + axisPointer snapping.
      config.strips.forEach((strip, i) => {
        if (strip.kind !== 'scalar') return
        for (const src of [allData, compareAllData]) {
          if (!src) continue
          const pd = src[strip.param]
          if (pd) {
            series.push(...invisibleSeries(pd).map(s => ({ ...s, xAxisIndex: i + 1, yAxisIndex: i + 1, silent: false })))
          }
        }
      })
    }
    const totalStripH = stripHeights.reduce((a, b) => a + b, 0)
    const setOptStart = performance.now()
    chartRef.current.setOption({
      animation: false,
      useUTC: true,
      backgroundColor: 'transparent',
      grid: isFilledPlume ? [
        { left: 25, right: 20, top: 10, bottom: totalStripH + 30 },
        ...stripHeights.map((h, i) => ({
          left: 25, right: 20, height: h,
          bottom: stripHeights.slice(i + 1).reduce((a, b) => a + b, 0) + 30,
        })),
      ] : { left: 10, right: 20, top: 10, bottom: 30, containLabel: true },
      // Ticks/labels sit at pin-local midnights; splitLine stays off as before.
      xAxis: isFilledPlume ? [
        { type: 'time' as const, gridIndex: 0, splitLine: { show: false }, axisLabel: { show: false }, axisTick: { show: false } },
        ...stripHeights.map((_h, i) => {
          const isLast = i === stripHeights.length - 1
          return {
            type: 'time' as const, gridIndex: i + 1, splitLine: { show: false },
            ...(isLast
              ? { axisTick: { customValues: dayTicks }, axisLabel: { customValues: dayTicks, hideOverlap: true, formatter: (v: number) => dayFmt.format(v) } }
              : { axisLabel: { show: false }, axisTick: { show: false }, axisLine: { show: false } }),
          }
        }),
      ] : {
        type: 'time' as const,
        splitLine: { show: false },
        axisTick: { customValues: dayTicks },
        axisLabel: { customValues: dayTicks, hideOverlap: true, formatter: (v: number) => dayFmt.format(v) },
      },
      yAxis: isFilledPlume ? [
        { type: 'value' as const, gridIndex: 0, name: chartData.unit, nameLocation: 'middle' as const, nameGap: 40, splitLine: { lineStyle: { color: 'rgba(0,0,0,0.06)' } } },
        ...stripHeights.map((_h, i) => ({ type: 'value' as const, gridIndex: i + 1, show: false })),
      ] : {
        type: 'value' as const,
        name: isPrecip ? (precipMode === 'rate' ? 'mm/h' : 'mm') : chartData.unit,
        nameLocation: 'middle' as const,
        nameGap: 40,
        splitLine: { lineStyle: { color: (isPlume || isPrecip) ? 'rgba(0,0,0,0.06)' : '#eee' } },
      },
      ...(isFilledPlume ? { axisPointer: { link: [{ xAxisIndex: Array.from({ length: stripHeights.length + 1 }, (_, i) => i) }] } } : {}),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
        position: () => {
          const cs = (chartRef.current as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number } } } } } | null)?.getModel().getComponent('grid', 0)?.coordinateSystem
          const r = cs?.getRect()
          return r ? [r.x + 8, r.y + 8] : [10, 10]
        },
        backgroundColor: 'rgba(255,255,255,0.95)',
        borderColor: '#ddd',
        textStyle: { fontSize: 12 },
        formatter: (params: unknown) => {
          const ps = params as { axisValue: number; value: [number, number] }[]
          if (!ps?.length) return ''
          const axisTime = ps[0].axisValue
          const time = tooltipFmt.format(axisTime)

          const fmtUnit = (u: string) => u && !u.startsWith('°') ? ` ${u}` : u
          const fmt = (s: { p10: number; p50: number; p90: number }, unit: string) =>
            `<b>${s.p50.toFixed(1)}</b> (${s.p10.toFixed(1)} – ${s.p90.toFixed(1)})${fmtUnit(unit)}`

          if (config.centerpiece.kind === 'filled-plume') {
            const { top, bot } = config.centerpiece
            const tStats = allData[top] ? ensembleStats(allData[top].traces, axisTime) : null
            const dStats = allData[bot] ? ensembleStats(allData[bot].traces, axisTime) : null
            const tUnit = allData[top]?.unit || ''
            const dUnit = allData[bot]?.unit || ''
            const lines = [time]
            if (tStats) lines.push(`T&nbsp;&nbsp;${fmt(tStats, tUnit)}`)
            if (dStats) lines.push(`Td&nbsp;${fmt(dStats, dUnit)}`)
            return lines.join('<br/>')
          }

          const stats = ensembleStats(data.traces, axisTime)
          const unit = chartData.unit || ''
          if (stats) return `${time}<br/>${fmt(stats, unit)}`

          // Fallback: no ensemble — show first value
          const vals = ps.map(p => p.value?.[1]).filter(v => v != null)
          if (!vals.length) return time
          return `${time}<br/><b>${vals[0].toFixed(1)}${fmtUnit(unit)}</b>`
        },
      },
      series,
    }, true)

    console.log(`setOption: ${(performance.now() - setOptStart).toFixed(0)}ms (${series.length} series)`)

    // Centerpiece density overlay
    if (isPlume && densityRef.current && shading === 'kde') {
      renderDensity(densityRef.current, chartRef.current, chartData)
    } else if (isPlume && densityRef.current && shading === 'contour') {
      renderContour(densityRef.current, chartRef.current, chartData)
    } else if (isPlume && densityRef.current && shading === 'adaptive') {
      renderAdaptiveContour(densityRef.current, chartRef.current, chartData)
    } else if (isPlume && densityRef.current && shading === 'daily') {
      renderDailyExtremes(densityRef.current, chartRef.current, chartData, pinTz)
    } else if (config.centerpiece.kind === 'filled-plume' && densityRef.current) {
      renderFilledPlume(densityRef.current, chartRef.current, allData, config.centerpiece.top, config.centerpiece.bot)
    } else if (isPrecip && densityRef.current) {
      renderPrecip(densityRef.current, chartRef.current, chartData)
    } else if (densityRef.current) {
      const dCtx = densityRef.current.getContext('2d')
      if (dCtx) { densityRef.current.width = 0; densityRef.current.height = 0 }
    }

    // Wind roses below chart for tabs whose only strip is a wind-rose
    // (the legacy ``wind`` tab). Inline-rose-in-strip-stack TODO.
    if (hasRoseStrip && !isFilledPlume && roseRef.current) {
      const rose = config.strips.find(s => s.kind === 'wind-rose') as { u: string; v: string }
      renderWindRoses(roseRef.current, chartRef.current, allData, rose.u, rose.v)
    } else if (roseRef.current) {
      roseRef.current.width = 0; roseRef.current.height = 0
    }

    // Render strip canvases — each strip's canvas is absolutely
    // positioned over its grid's y so it visually occupies the right
    // band in the chart. Strip count is config-driven.
    if (isFilledPlume && chartRef.current && config.centerpiece.kind === 'filled-plume') {
      const getGridRect = (idx: number) =>
        (chartRef.current as unknown as { getModel(): { getComponent(t: string, i: number): { coordinateSystem: { getRect(): { x: number; y: number; width: number; height: number } } } } }).getModel().getComponent('grid', idx)?.coordinateSystem?.getRect()

      const topTraces = allData[config.centerpiece.top]?.traces ?? []
      if (topTraces.length) {
        let mXMin = Infinity, mXMax = -Infinity
        for (const t of topTraces) {
          for (const ms of t.times) {
            if (ms < mXMin) mXMin = ms
            if (ms > mXMax) mXMax = ms
          }
        }
        const mDataLeft = Math.round(chartRef.current.convertToPixel('grid', [mXMin, 0])[0])
        const mDataRight = Math.round(chartRef.current.convertToPixel('grid', [mXMax, 0])[0])
        const mDataW = mDataRight - mDataLeft

        config.strips.forEach((strip, i) => {
          const canvas = stripRefs.current[i]
          const rect = getGridRect(i + 1)
          if (!canvas || !rect) return
          canvas.style.top = `${rect.y}px`
          renderStripCanvas(strip, canvas, chartRef.current!, allData,
                            mXMin, mXMax, mDataLeft, mDataW)
        })
      }
    } else {
      for (const canvas of stripRefs.current) {
        if (canvas) { canvas.width = 0; canvas.height = 0 }
      }
    }
  }, [data, config, shading, windUnit, precipMode, compareData, allData, compareAllData, resizeKey])

  // Wind roses BELOW the chart (legacy wind-tab layout) when the only
  // strip is a rose. Inline-rose-in-strip-stack lands later.
  const showRoses = config.strips.some(s => s.kind === 'wind-rose') && config.centerpiece.kind !== 'filled-plume'

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <canvas ref={densityRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />
        {config.strips.map((_strip, i) => (
          <canvas
            key={i}
            ref={el => { stripRefs.current[i] = el }}
            style={{ position: 'absolute', left: 0, width: '100%', pointerEvents: 'none' }}
          />
        ))}
        <div ref={containerRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
      </div>
      {showRoses && (
        <canvas ref={roseRef} style={{ width: '100%', height: 50, flexShrink: 0 }} />
      )}
    </div>
  )
}
